import { CommonModule, CurrencyPipe, PercentPipe } from '@angular/common';
import { Component, Input } from '@angular/core';
import dayjs from 'dayjs';
import { TooltipModule } from 'primeng/tooltip';

import { PacingData, ViewDatesQuery } from '../../../generated/graphql.generated';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';
import { copyTextToClipboard } from '../../utilities/clipboard.util';

export type PacingLayout = 'card' | 'strip';

/** Cents. LY-today snapshots at or below this count as empty (backend PACING_REVENUE_THRESHOLD). */
const LY_TODAY_EMPTY_THRESHOLD = 100;

/**
 * The LY value performance is measured against: LY Today, or LY Final when the LY-today
 * snapshot is empty. Mirrors computePacingForDay in backend find.pacing.ts.
 */
export function pacingBaseline(lyToday: number, lyFinal: number): number {
  return lyToday > LY_TODAY_EMPTY_THRESHOLD ? lyToday : lyFinal;
}

type ViewDay = NonNullable<ViewDatesQuery['viewDates']>[0];

@Component({
  selector: 'app-pacing-card',
  standalone: true,
  imports: [CommonModule, CurrencyPipe, PercentPipe, TooltipModule],
  templateUrl: './pacing-card.component.html',
  styleUrls: ['./pacing-card.component.scss']
})
export class PacingCardComponent {
  @Input() pacing: PacingData | undefined | null;
  @Input() layout: PacingLayout = 'card';
  @Input() actualRevenue: number = 0;
  /** All per-day view-dates rows for the current view. When provided alongside a
   *  month-level `pacing` aggregate, clicking the copy icon writes a Google Sheets
   *  compatible TSV breakdown to the clipboard. */
  @Input() allDays: ViewDay[] | null = null;

  private readonly notify: FreyaNotificationsService;

  constructor(notify: FreyaNotificationsService) {
    this.notify = notify;
  }

  get showContent(): boolean {
    return !!this.pacing;
  }

  /** LY revenue the performance ratio divides by (see pacingBaseline). */
  get baseline(): number {
    return pacingBaseline(this.pacing?.lastYearRevenueToday ?? 0, this.pacing?.lastYearRevenueFinal ?? 0);
  }

  get baselineLabel(): string {
    return (this.pacing?.lastYearRevenueToday ?? 0) > LY_TODAY_EMPTY_THRESHOLD ? 'LY Today' : 'LY Final';
  }

  get baselineClass(): string {
    return this.baselineLabel === 'LY Today' ? 'ly-today' : 'ly-final';
  }

  /** The week-aligned last-year day for a single-day row; month aggregates have none. */
  get lyDayLabel(): string {
    const date = this.pacing?.lastYearDate;
    return date ? dayjs(date).format('ddd, MMM D, YYYY') : 'the same days last year';
  }

  get hasBaseline(): boolean {
    return this.baseline > 0;
  }

  get canCopy(): boolean {
    return this.layout === 'strip' && !!this.allDays?.length && !!this.pacing;
  }

  /** The strip box IS the copy control (Ray): one click anywhere on the
   *  pacing box writes a Google Sheets compatible TSV to the clipboard —
   *  no separate copy icon. */
  async copyPacingToClipboard(): Promise<void> {
    if (!this.canCopy) { return; }

    const tsv = this.buildTsv();

    const copied = await copyTextToClipboard(tsv);
    if (copied) {
      this.notify.success('Pacing data copied to clipboard');
    } else {
      this.notify.error('Failed to copy pacing data');
    }
  }

  private fmt(cents: number | null | undefined): string {
    const val = (cents ?? 0) / 100;
    return val.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  }

  private pct(ratio: number | null | undefined): string {
    if (ratio == null) { return '—'; }
    return `${Math.round(ratio * 100)}%`;
  }

  private buildTsv(): string {
    const p = this.pacing!;
    const lines: string[] = [];

    // ── Month summary header ──────────────────────────────────────────────────
    // Projected Final = Performance × LY Final. LY columns are week-aligned (same ISO week and weekday last year).
    lines.push('MONTHLY SUMMARY');
    lines.push([
      'Current',
      'Projected Final',
      'LY Today',
      'LY Final',
      'Performance',
    ].join('\t'));

    lines.push([
      this.fmt(this.actualRevenue),
      this.fmt(p.pacingRevenue),
      this.fmt(p.lastYearRevenueToday),
      this.fmt(p.lastYearRevenueFinal),
      this.pct(p.performanceToday),
    ].join('\t'));

    lines.push('');

    // ── Per-day breakdown ─────────────────────────────────────────────────────
    lines.push('DAILY BREAKDOWN');
    lines.push([
      'Date',
      'LY Date',
      'Current',
      'Projected Final',
      'LY Today',
      'LY Final',
      'Performance',
    ].join('\t'));

    const daysWithPacing = this.allDays!.filter(d => d.pacing);

    for (const day of daysWithPacing) {
      const dp = day.pacing!;
      lines.push([
        day.date,
        dp.lastYearDate ?? '',
        this.fmt(day.totalRevenue),
        this.fmt(dp.pacingRevenue),
        this.fmt(dp.lastYearRevenueToday),
        this.fmt(dp.lastYearRevenueFinal),
        this.pct(dp.performanceToday),
      ].join('\t'));
    }

    return lines.join('\n');
  }
}
